import React, { useEffect, useState, useRef } from 'react';

import CtaWidget from '../../../../CtaWidget';
import { ModalNavCategory } from './MobileNavCategory';

import { MobileNavProps } from '../index';

interface Props extends MobileNavProps {
  isMenuOpen: boolean;
  toggleModal: () => void;
  isBannerVisible?: boolean;
}

export function MobileModal({
  navItems,
  modalMobileCta,
  isMenuOpen,
  isBannerVisible = false,
  toggleModal
}: Props) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const categoryExpanded = navItems?.find(
    (item: any) => item.fields.isExpanded === true
  ) as any;
  const [titleExpanded, setTitleExpanded] = React.useState<string | undefined>(
    categoryExpanded?.fields?.name
  );
  const [topOffset, setTopOffset] = useState(56);
  const [ctaHeight, setCtaHeight] = useState(0);
  const ctaRef = useRef<HTMLDivElement>(null);
  const originalBodyStylesRef = useRef<{
    overflow: string;
    position: string;
    width: string;
  } | null>(null);

  const restoreBodyStyles = React.useCallback(() => {
    if (typeof window === 'undefined' || !originalBodyStylesRef.current) {
      return;
    }
    document.body.style.overflow = originalBodyStylesRef.current.overflow;
    document.body.style.position = originalBodyStylesRef.current.position;
    document.body.style.width = originalBodyStylesRef.current.width;
    originalBodyStylesRef.current = null;
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (isMenuOpen) {
      const computedStyle = window.getComputedStyle(document.body);
      originalBodyStylesRef.current = {
        overflow: computedStyle.overflow,
        position: computedStyle.position,
        width: computedStyle.width
      };

      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';

      const calculateTopOffset = () => {
        if (typeof document === 'undefined') {
          return;
        }

        const navbar = document.querySelector(
          '[data-testid="mobile-nav-cta"]'
        ) as HTMLElement;

        let offset = 56;

        if (navbar) {
          offset = navbar.offsetHeight;
        }

        if (isBannerVisible) {
          const banner = document.querySelector(
            '[data-testid="webBanner"]'
          ) as HTMLElement;
          if (banner && banner.offsetHeight > 0) {
            offset += banner.offsetHeight;
          }
        }

        setTopOffset(offset);
      };

      calculateTopOffset();

      const resizeObserver = new ResizeObserver(() => {
        calculateTopOffset();
      });

      const navbar = document.querySelector('[data-testid="mobile-nav-cta"]');

      if (navbar) resizeObserver.observe(navbar);
      if (isBannerVisible) {
        const banner = document.querySelector('[data-testid="webBanner"]');
        if (banner) resizeObserver.observe(banner);
      }

      window.addEventListener('resize', calculateTopOffset);

      return () => {
        restoreBodyStyles();
        window.removeEventListener('resize', calculateTopOffset);
        resizeObserver.disconnect();
      };
    } else {
      restoreBodyStyles();
    }
  }, [isMenuOpen, isBannerVisible, restoreBodyStyles]);

  useEffect(() => {
    return () => {
      restoreBodyStyles();
    };
  }, [restoreBodyStyles]);

  useEffect(() => {
    if (!isMenuOpen || !modalMobileCta) {
      setCtaHeight(0);
      return;
    }

    const updateCtaHeight = () => {
      if (ctaRef.current) {
        const height = ctaRef.current.offsetHeight;
        setCtaHeight(height);
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      updateCtaHeight();
    });

    const timeoutId = setTimeout(() => {
      updateCtaHeight();
      if (ctaRef.current) {
        resizeObserver.observe(ctaRef.current);
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [isMenuOpen, modalMobileCta]);

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[1000] flex w-screen items-center justify-center bg-white p-0"
      style={{ top: `${topOffset}px`, height: `calc(100vh - ${topOffset}px)` }}
      aria-label="Navigation menu"
      data-testid="mobile-modal-navigation"
      aria-modal="true"
    >
      <div className="flex h-full w-full flex-col outline-0">
        <div
          className="flex-1 overflow-y-auto py-5"
          style={{
            minHeight: 0,
            WebkitOverflowScrolling: 'touch'
          }}
          data-testid="mobile-modal"
        >
          {navItems &&
            navItems.map(({ fields }: any, idx: number) => (
              <ModalNavCategory
                {...fields}
                setTitleExpanded={setTitleExpanded}
                titleExpanded={titleExpanded}
                toggleModal={toggleModal}
                key={idx}
              />
            ))}
          {modalMobileCta && ctaHeight > 0 && (
            <div
              style={{
                height: `${(ctaHeight + (isBannerVisible ? 60 : 40)) * 1.5}px`,
                flexShrink: 0
              }}
              aria-hidden="true"
            />
          )}
        </div>

        {modalMobileCta && (
          <div
            ref={(el) => {
              (
                ctaRef as React.MutableRefObject<HTMLDivElement | null>
              ).current = el;
              if (el) {
                requestAnimationFrame(() => {
                  const height = el.offsetHeight;
                  if (height > 0) {
                    setCtaHeight(height);
                  }
                });
              }
            }}
            className="fixed bottom-0 left-0 right-0 flex w-full flex-col items-center justify-center bg-[#F2F5F5] px-6 py-8"
          >
            <CtaWidget
              {...modalMobileCta?.fields}
              id="mobile-modal-nav-cta"
              sysId={modalMobileCta?.sys?.contentType?.sys?.id}
              module="Navigation"
              errorPosition="top"
            />
          </div>
        )}
      </div>
    </div>
  );
}
